/** Side of the square avatar we store. Big enough for a 2x retina `size-24`. */
const AVATAR_PX = 256
/** The API refuses data URLs longer than this (JSON body limit). */
const AVATAR_MAX_CHARS = 90_000

/** Centre-crop an image file to a small square and encode it as a data URL the API accepts. */
export async function avatarDataUrl(file: File): Promise<string> {
    const bitmap = await createImageBitmap(file)
    const side = Math.min(bitmap.width, bitmap.height)
    const canvas = document.createElement('canvas')
    canvas.width = AVATAR_PX
    canvas.height = AVATAR_PX
    const context = canvas.getContext('2d')
    if (!context) {
        throw new Error('canvas_unavailable')
    }
    context.drawImage(
        bitmap,
        (bitmap.width - side) / 2,
        (bitmap.height - side) / 2,
        side,
        side,
        0,
        0,
        AVATAR_PX,
        AVATAR_PX,
    )
    bitmap.close()
    for (const quality of [0.9, 0.8, 0.65, 0.5]) {
        let dataUrl = canvas.toDataURL('image/webp', quality)
        if (!dataUrl.startsWith('data:image/webp')) {
            dataUrl = canvas.toDataURL('image/jpeg', quality)
        }
        if (dataUrl.length <= AVATAR_MAX_CHARS) {
            return dataUrl
        }
    }
    throw new Error('avatar_too_large')
}

/** `Mary-Jane van Dyke` → `MD`; a single name gives one letter. */
export function initials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean)
    const first = words[0]?.[0] ?? ''
    const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
    return `${first}${last}`.toUpperCase()
}
